/**
 * Extra confirmation for vendor-scope rules whose match text is a short
 * token or a generic payment word. Distinctive shorts (3M, HEB) stay
 * creatable after confirm — this is not a refuse list.
 */

export const GENERIC_PAYMENT_TOKENS: ReadonlySet<string> = new Set([
  "POS",
  "ACH",
  "DEBIT",
  "CREDIT",
  "CHECK",
  "WIRE",
  "TRANSFER",
  "PURCHASE",
  "PAYMENT",
  "PAYPAL",
  "SQUARE",
  "VENMO",
  "ZELLE",
  "STRIPE",
  "CLOVER",
]);

export const SHORT_VENDOR_TOKEN_MAX_LEN = 4;

export const VENDOR_SCOPE_EXTRA_CONFIRM_LABEL =
  "This match is a short or generic payment token. Create the vendor-scope rule anyway.";

export const VENDOR_SCOPE_EXTRA_CONFIRM_HELP =
  "A vendor-scope rule matches this token as a whole word. Short and generic payment words appear in many merchants — confirm before creating.";

/**
 * Extra confirm is required when the AM is creating a vendor-scope rule
 * and any token is a generic payment word, or the whole match is a single
 * token shorter than 5 characters. Keying on the whole string made
 * "ACH DEBIT" quieter than "ACH" (R31O-3). Multi-word distinctive names
 * skip. This is a confirm, never a refuse — 3M / HEB / Cedar Rapids stay
 * creatable.
 */
export function vendorScopeNeedsExtraConfirm(matchText: string): boolean {
  const trimmed = matchText.trim();
  if (!trimmed) return false;
  const tokens = trimmed.split(/\s+/);
  if (tokens.some((token) => GENERIC_PAYMENT_TOKENS.has(token.toUpperCase()))) {
    return true;
  }
  if (tokens.length === 1 && trimmed.length < SHORT_VENDOR_TOKEN_MAX_LEN + 1) {
    return true;
  }
  return false;
}
